import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useLocation } from 'react-router-dom';

import i18n from '@nextroll/ar-i18n';
import { useContainerContext } from '@nextroll/ar-container-context';
import { Alerts } from '@nextroll/ar-style-base';

import { useRoflStore } from 'store/roflStore';
import {
    IWorkflow, IWorkflowSchema, IWorkflowSchemaTrigger,
    WorkflowStatusType, WorkflowBaseType,
    WORKFLOW_BASE, WORKFLOW_NODE, WORKFLOW_STATUS, WORKFLOW_TRIGGER,
} from 'types/rofl';
import { createWorkflow, updateWorkflow, toggleWorkflowStatus } from 'api/workflowsApi';
import useRequest, { IHandlerParams } from 'hooks/useRequest';
import { NotificationContext } from 'hooks/useNotification';
import { formatTimeShort } from 'util/util';
import { getWorkflowsFeature } from 'util/featureFlags';
import {
    WORKFLOW_MAX_LIMIT_REACHED_ERROR, WORKFLOW_DEFAULT_MAX_LIMIT, WORKFLOW_SCHEDULE_DEFAULT, WORKFLOW_CLONE_TEMP_EID,
} from 'util/workflow.constants';

import { ConfirmDialog } from 'components/basic/ConfirmDialog/ConfirmDialog';
import { EditableName } from 'components/basic/EditableName/EditableName';
import { NodeContainer } from 'components/basic/NodeContainer/NodeContainer';
import { SaveButton } from 'components/basic/SaveButton/SaveButton';
import { SettingsPanel } from 'components/basic/SettingsPanel/SettingsPanel';


/**
 * Static listener for detect window / tab closing without saving.
 */
const beforeUnloadListener = (event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = 'Are you sure you want to exit?';
};

/**
 * WorkflowPostBody are the params for saving a workflow
 */
export interface WorkflowPostBody {
    name: string;
    status: WorkflowStatusType;
    schema: IWorkflowSchema;
    entity_type?: WorkflowBaseType;
    template_eid?: string;
}

interface IProps {
    workflow?: Partial<IWorkflow>;
    setWorkflow: (workflow: Partial<IWorkflow> | undefined) => void;
    loadingWorkflow: boolean;
    onClose?: () => void;
}

export const CanvasPage = (props: IProps) => {
    const { workflow, setWorkflow, loadingWorkflow, onClose } = props;

    const baseFromUrl = new URLSearchParams(useLocation().search)?.get('base');
    const { advertisable: { eid: advertisableEid } = {}, setPageTitle } = useContainerContext();
    const { addAlert } = useContext(NotificationContext);

    // max number of workflows that allowed to create; if the value is missing in the feature, then set to 25
    const maxOnWorkflowsLimit = getWorkflowsFeature().quantity || WORKFLOW_DEFAULT_MAX_LIMIT;

    // control states for rendering
    const [tab, setTab] = useState('builder' as 'builder' | 'settings');
    const [validate, setValidate] = useState(false);
    const [openConfirmCloseDialog, setOpenConfirmCloseDialog] = useState(false); // confirm to close canvas
    const [loadingTurnOn, setLoadingTurnOn] = useState(false); // during POST or PUT
    const [loadingSave, setLoadingSave] = useState(false); // during POST or PUT
    const [lastSaved, setLastSaved] = useState(undefined as string | undefined);

    // extra variables of the workflow
    const [name, setName] = useState(workflow?.name || 'My Workflow');
    const [schedule, setSchedule] = useState(workflow?.schema?.trigger?.cron_expression || WORKFLOW_SCHEDULE_DEFAULT);
    const [includeCurrentEntities, setIncludeCurrentEntities]
        = useState(workflow?.schema?.trigger?.include_current_entities_in_first_run ?? false);
    const [reentryAfter, setReentryAfter] = useState(workflow?.schema?.trigger?.reentry_after || undefined);
    const [hasChanged, setHasChanged] = useState(false);
    /**
     * This is the node data of the given workflow. Entire UI rendering is driven by it.
     */
    const [
        base,
        contentData,
        topology,
        updateCrm,
        updateAuthentications,
        updateBase,
        updateContentDataFromWorkflow,
    ] = useRoflStore(useShallow(state => [
        state.base,
        state.contentData,
        state.topology,
        state.updateCrm,
        state.updateAuthentications,
        state.updateBase,
        state.updateContentDataFromWorkflow,
    ]));

    /**
     * get basic auth info such as the default CRM and connected services (Salesforce, HubSpot, etc.)
     */
    useEffect(() => {
        if (!advertisableEid) {
            return; // system not ready; do nothing
        }
        updateCrm(advertisableEid);
        updateAuthentications(advertisableEid);
    }, [advertisableEid, updateCrm, updateAuthentications]);

    /**
     * sync workflow data between the internal store and the updates in props
     */
    useEffect(() => {
        updateContentDataFromWorkflow(workflow);
    }, [updateContentDataFromWorkflow, workflow, base]);

    /**
     * update trigger base when parachusing from a URL
     */
    useEffect(() => {
        if (baseFromUrl !== WORKFLOW_BASE.CONTACTS && baseFromUrl !== WORKFLOW_BASE.ACCOUNTS) {
            return;
        }
        if (baseFromUrl !== base) {
            updateBase(baseFromUrl);
        }
    }, [baseFromUrl, base, updateBase]);

    const handleCloseCanvas = useCallback(() => {
        updateContentDataFromWorkflow(undefined);
        setWorkflow(undefined);
        onClose?.();
    }, [updateContentDataFromWorkflow, setWorkflow, onClose]);

    /**
     * determine the initial status of the workflow when saving or turning on
     */
    const determineInitialStatus = useCallback((isSave: boolean, isDraft?: boolean) => {
        if (isDraft) {
            return WORKFLOW_STATUS.WORKFLOW_OFF; // draft workflow always starts as "off"
        }
        return workflow?.status || (isSave ? WORKFLOW_STATUS.WORKFLOW_OFF : WORKFLOW_STATUS.WORKFLOW_ON);
    }, [workflow?.status]);

    /**
     * compose the POST body for creating or updating a workflow
     */
    const createPostBody = useCallback((isSave: boolean, isDraft?: boolean) => {
        const triggerData = contentData?.trigger?.data as IWorkflowSchemaTrigger;
        const nodesContent = (Object.values(contentData || {}).find(node => node.data?.type === 'action-group') as any)?.data?.nodesContent;
        const actionData = [
            ...Object.entries(contentData).filter(([key]) => key !== WORKFLOW_NODE.TRIGGER),
            ...Object.values(nodesContent || {}).map((node: any) => [node.eid, { data: node }]),
        ].reduce((nodes, [key, node]) => ({
            ...nodes,
            [key]: node?.data || {},
        }), {});

        const postBody: WorkflowPostBody = {
            name,
            status: determineInitialStatus(isSave, isDraft),
            entity_type: base || WORKFLOW_BASE.CONTACTS,
            schema: {
                trigger: {
                    eid: WORKFLOW_NODE.TRIGGER,
                    contact_list_eid: triggerData?.contact_list_eid,
                    include_current_entities_in_first_run: includeCurrentEntities,
                    entity_type: base || WORKFLOW_BASE.CONTACTS,
                    reentry_after: reentryAfter,
                    cron_expression: schedule,
                    next: [topology[1]],
                    type: WORKFLOW_TRIGGER.RECURRING,

                    // rules are handled differently by the API between account-based vs. contact-based workflows
                    ...(base === WORKFLOW_BASE.ACCOUNTS ? {
                        config: {
                            list_rule: triggerData?.config?.list_rule,
                            not_in_crm_accounts: triggerData?.config?.not_in_crm_accounts,
                            not_in_sales_marketing: triggerData?.config?.not_in_sales_marketing,
                            not_in_advertising_filter: triggerData?.config?.not_in_advertising_filter,
                            not_in_intent: triggerData?.config?.not_in_intent,
                            exclusions_types: triggerData?.config?.exclusions_types,

                            // if `not_in_sales_marketing` is true, `not_in_sales_marketing_since` must be provided
                            ...(triggerData?.config?.not_in_sales_marketing ? { not_in_sales_marketing_since: 'P30D' } : {}),

                            // if `not_in_intent` is true, `not_in_intent_since` must be provided
                            ...(triggerData?.config?.not_in_intent ? { not_in_intent_since: 'P30D' } : {}),
                        },
                    } : {
                        rules: (!triggerData?.contact_list_eid && triggerData?.rules) || undefined,
                    }),
                },
                nodes: actionData,
            },
            template_eid: workflow?.template_eid,
        };
        return postBody;
    }, [
        contentData, name, base, schedule, includeCurrentEntities, reentryAfter, topology,
        workflow?.template_eid, determineInitialStatus,
    ]);

    /**
     * request wrapper that also handles notifications
     */
    const [call] = useRequest();

    /**
     * Last alert before closing the browser when the canvas is open.
     */
    useEffect(() => {
        if (hasChanged) {
            window.addEventListener('beforeunload', beforeUnloadListener, { capture: true });
        }

        return () => window.removeEventListener('beforeunload', beforeUnloadListener, { capture: true });
    }, [hasChanged]);

    /**
     * determine whether or not all node contents are completed
     */
    const allCompleted = useMemo(
        () => Object.values(contentData).reduce((result, curr) => result && !curr.isIncomplete, true),
        [contentData],
    );

    /**
     * determine whether or not it is an existing workflow
     * note we use a special EID for identifying in-progress cloned workflows
     */
    const isExistingWorkflow = !!workflow?.eid && workflow.eid !== WORKFLOW_CLONE_TEMP_EID;

    /**
     * determine whether or not the workflow can be saved as a draft
     */
    const canSaveDraft = !isExistingWorkflow || workflow?.status === WORKFLOW_STATUS.WORKFLOW_OFF;

    /**
     * JSX element for the top tabs,i.e. "builder" vs. "setting"
     */
    const tabs = useMemo(() => (
        <div className='ar-app-subnav-items-container'>
            <ul className='ar-app-subnav-items'>
                <li className={`ar-app-subnav-item${tab === 'builder' ? ' active' : ''}`}>
                    <a href='#builder'>{i18n.gettext('1. Builder')}</a>
                </li>
                <li className={`ar-app-subnav-item${tab === 'settings' ? ' active' : ''}`}>
                    <a href='#settings'>{i18n.gettext('2. Settings')}</a>
                </li>
            </ul>
        </div>
    ), [tab]);

    /**
     * Determine what happens after the "next" button is pressed.
     * - show in-completed errors if something is missing, or;
     * - proceed to the setting page
     * Note this action only applies when creating a new workflow.
     */
    const handleNext = useCallback(
        () => (allCompleted ? setTab('settings') : setValidate(true)),
        [allCompleted, setTab, setValidate],
    );

    const handleSave = useCallback((isSave: boolean = false) => async () => {
        if (!advertisableEid) {
            return; // no valid advertisable ID; do nothing
        }

        // POST body; check here to verify the request body
        const postBody = createPostBody(isSave, isSave && canSaveDraft /* isDraft */);

        const setLoading = isSave ? setLoadingSave : setLoadingTurnOn;
        setLoading(true);
        await call(async () => {
            if (!isExistingWorkflow) {
                // workflow does not exist; create a new one (a running workflow OR a draft)
                await createWorkflow(advertisableEid, postBody);
                handleCloseCanvas?.(); // close the canvas after POST
            } else if (!isSave) {
                // workflow exists but it is not saving; meaning it's turning on a draft workflow
                await toggleWorkflowStatus(workflow?.eid as string, advertisableEid, WORKFLOW_STATUS.WORKFLOW_ON);
                handleCloseCanvas?.(); // close the canvas after PUT
            } else {
                // workflow exists and it is saving the current progress
                await updateWorkflow(workflow?.eid as string, advertisableEid, postBody);
            }
        }, {
            errorHandler: requestError => {
                // hijack the error handling for a special case
                if (requestError.parsedBody?.code === WORKFLOW_MAX_LIMIT_REACHED_ERROR) {
                    addAlert?.({
                        type: Alerts.ALERT_TYPE_ERROR,
                        title: i18n.gettext('This workflow can\'t be created due to plan limits'),
                        message: i18n.gettext(
                            'You reached your {{ limit }} running workflows limit. Turn off one of your running workflows or contact your Account Manager to increase your limit.',
                            { limit: maxOnWorkflowsLimit },
                        ),
                    });
                    return true;
                }
                return false;
            },
            notificationOptions: {
                successNotificationTitle: (canSaveDraft && isSave) ? i18n.gettext('Progress saved for {{ name }}', { name }) : i18n.gettext('Workflow has been saved'),
                successNotificationMessage: (canSaveDraft && isSave)
                    ? i18n.gettext('Your progress has been saved, and the workflow will remain off. You can finish setup and turn it on anytime.')
                    : i18n.gettext('Changes have been applied to "{{ name }}" workflow.', { name }),
                errorNotificationTitle: i18n.gettext('Error occurred when saving the workflow.'),
            } } as IHandlerParams);
        setLastSaved(formatTimeShort());
        setHasChanged(false);
        setLoading(false);
    }, [
        advertisableEid, name, isExistingWorkflow, canSaveDraft, workflow?.eid, maxOnWorkflowsLimit,
        createPostBody, call, handleCloseCanvas, addAlert,
    ]);

    const handleUpdateName = useCallback((title: string) => {
        if (!title) {
            return; // cannot set an empty workflow title; do nothing
        }
        setName(title);
        setPageTitle?.({
            title: i18n.gettext('AdRoll'),
            subtitle: title,
        });
    }, [setPageTitle]);

    /**
     * JSX elements for buttons at the top right corner, i.e. "next", "turn on", and "save"
     */
    const btnNext = useMemo(() => (
        <button className='btn btn-primary' disabled={loadingTurnOn} type='button' onClick={handleNext}>
            {i18n.gettext('Next')}
        </button>
    ), [loadingTurnOn, handleNext]);
    const btnSubmit = useMemo(() => {
        if (!canSaveDraft) {
            return null; // show the "turn on" button only if the workflow is off
        }
        return (
            <button className='btn btn-primary' disabled={loadingTurnOn} type='button' onClick={handleSave(false)}>
                {i18n.gettext('Turn Workflow On')}
            </button>
        );
    }, [canSaveDraft, loadingTurnOn, handleSave]);
    const btnSave = useMemo(() => {
        const label = canSaveDraft ? i18n.gettext('Save Draft') : undefined;
        return <SaveButton disabled={loadingSave} onClick={handleSave(true)} loading={loadingSave} label={label} />;
    }, [canSaveDraft, loadingSave, handleSave]);

    /**
     * JSX element for the Save status header message
     */
    const saveStatusMessage = useMemo(() => {
        let content;
        if (lastSaved) {
            content = i18n.gettext('Saved at {{ lastSaved }}', { lastSaved });
        }
        if (hasChanged) {
            content = i18n.gettext('Unsaved changes');
        }

        return workflow?.eid && content && <small>{content}</small>;
    }, [hasChanged, lastSaved, workflow?.eid]);

    return (
        <>
            <div className='workflows-canvas-page-header'>
                <div className='workflows-canvas-page-header-title'>
                    <EditableName name={name} onUpdateName={handleUpdateName} />
                </div>

                {tabs}

                <div className='workflows-canvas-page-header-btn-group'>
                    {saveStatusMessage}
                    <button
                        className='btn btn-default'
                        // We are not adding disabled property because since saving takes so little time in M1.
                        // It looks like a flicker, so we are skipping it.
                        onClick={() => {
                            if (loadingSave || loadingTurnOn) {
                                return; // do nothing during POST or PUT a workflow
                            }
                            hasChanged ? setOpenConfirmCloseDialog(true) : handleCloseCanvas();
                        }}
                        type='button'
                    >
                        {i18n.gettext('Close')}
                    </button>
                    {tab === 'builder' && btnNext}
                    {tab !== 'builder' && btnSave}
                    {tab !== 'builder' && btnSubmit}
                </div>
            </div>

            {/* list of nodes to render in the canvas */}

            {tab === 'builder' && (
                <NodeContainer
                    initialActiveNodeId={
                        // If we are dealing with a template, we want to show the trigger panel opened first.
                        (!workflow?.eid && workflow?.template_eid) ? WORKFLOW_NODE.TRIGGER : undefined
                    }
                    loadingWorkflow={loadingWorkflow}
                    setHasChanged={setHasChanged}
                    validate={validate}
                    validationErrors={workflow?.errors?.schema_errors}
                    validationWarnings={workflow?.warnings?.schema_warnings}
                />
            )}
            {tab === 'settings' && (
                <SettingsPanel
                    readonly={isExistingWorkflow}
                    name={name}
                    setName={setName}
                    schedule={schedule}
                    setSchedule={setSchedule}
                    includeCurrentEntities={includeCurrentEntities}
                    setIncludeCurrentEntities={setIncludeCurrentEntities}
                    reentryAfter={reentryAfter}
                    setReentryAfter={setReentryAfter}
                    onBack={() => setTab('builder')}
                />
            )}

            <ConfirmDialog
                show={openConfirmCloseDialog}
                onClose={() => setOpenConfirmCloseDialog(false)}
                title={i18n.gettext('Leave Workflow Setup')}
                description={i18n.gettext('If you leave before completing the setup, your workflow will not be saved.')}
                isReverseActions
                btnConfirmLabel={i18n.gettext('Leave')}
                btnCancelLabel={i18n.gettext('Stay')}
                request={handleCloseCanvas}
            />
        </>
    );
};
